// /frontend/src/components/MessageThread.jsx
// State-of-the-art iMessage-style SMS conversation
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { addComm } from "../api";
import { X, Send, CheckCheck, Clock, AlertCircle, MessageCircle, ClipboardCheck, FolderPlus, Check, Loader2 } from "lucide-react";
import SMSOutcomeModal from "./SMSOutcomeModal";

const API_BASE = process.env.REACT_APP_API_URL || '';

const MessageThread = ({ channel, thread, onClose, unread = 0, clientName, clientId, clientPhone }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [showSMSOutcome, setShowSMSOutcome] = useState(false);
  const [savingMedia, setSavingMedia] = useState({});
  const inputRef = useRef(null);

  const handleSaveToDocuments = async (commId, mediaIndex) => {
    const key = `${commId}-${mediaIndex}`;
    setSavingMedia(prev => ({ ...prev, [key]: 'saving' }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/uploads/save-from-comm/${commId}/${mediaIndex}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ label: 'MMS Attachment' })
      });
      setSavingMedia(prev => ({ ...prev, [key]: res.ok ? 'saved' : 'error' }));
    } catch {
      setSavingMedia(prev => ({ ...prev, [key]: 'error' }));
    }
  };

  // Fetch message history
  const fetchMessages = useCallback(async () => {
    if (!clientId) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/clients/${clientId}/comms/all`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        // Filter to SMS only and sort oldest first
        const smsMessages = (data.data || [])
          .filter(c => c.type === 'sms')
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(smsMessages);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleSend = async () => {
    if (!msg.trim() || sending) return;

    setSending(true);
    setError(null);

    try {
      await addComm(clientId, {
        type: 'sms',
        subject: '',
        body: msg.trim(),
        direction: 'out'
      });

      // Add optimistic update
      const newMessage = {
        id: Date.now(),
        type: 'sms',
        direction: 'outbound',
        body: msg.trim(),
        created_at: new Date().toISOString(),
        metadata: { status: 'sending' }
      };
      setMessages(prev => [...prev, newMessage]);
      setMsg("");

      // Refresh to get actual status
      setTimeout(fetchMessages, 1000);
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
  };

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const getDeliveryStatus = (msg) => {
    const meta = msg.metadata || {};
    const status = meta.deliveryStatus || meta.status;

    if (status === 'delivered') return { icon: CheckCheck, color: 'text-blue-500' };
    if (status === 'sent') return { icon: CheckCheck, color: 'text-gray-400' };
    if (status === 'failed') return { icon: AlertCircle, color: 'text-red-500' };
    if (status === 'sending' || status === 'queued') return { icon: Clock, color: 'text-gray-400' };
    return { icon: CheckCheck, color: 'text-gray-300' };
  };

  // Group messages by date
  const groupedMessages = [];
  let currentDate = null;

  messages.forEach(msg => {
    const msgDate = new Date(msg.created_at).toDateString();
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groupedMessages.push({ type: 'date', date: msg.created_at });
    }
    groupedMessages.push({ type: 'message', ...msg });
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
              {clientName?.charAt(0) || '?'}
            </div>
            <div>
              <h3 className="font-semibold text-white text-lg">{clientName || 'Client'}</h3>
              <p className="text-sm text-slate-400">{clientPhone || 'SMS Conversation'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto flex flex-col-reverse px-4 py-4 bg-gradient-to-b from-gray-100 to-gray-50" style={{ minHeight: '300px' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageCircle className="w-16 h-16 mb-3 text-gray-300" />
              <p className="text-lg font-medium">No messages yet</p>
              <p className="text-sm">Send a message to start the conversation</p>
            </div>
          ) : (
            <div className="space-y-1">
              {groupedMessages.map((item, idx) => {
                if (item.type === 'date') {
                  return (
                    <div key={`date-${idx}`} className="flex justify-center py-3">
                      <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                        {formatDateHeader(item.date)}
                      </span>
                    </div>
                  );
                }

                const message = item;
                const isOutbound = message.direction === 'outbound' || message.direction === 'out';
                const status = getDeliveryStatus(message);

                return (
                  <div
                    key={message.id || idx}
                    className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-2`}
                  >
                    <div className="max-w-[80%]">
                      <div
                        className={`rounded-2xl shadow-sm overflow-hidden ${
                          isOutbound
                            ? 'bg-amber-500 text-white rounded-br-md'
                            : 'bg-white text-gray-900 rounded-bl-md border border-gray-100'
                        }`}
                      >
                        {/* MMS Media */}
                        {message.metadata?.mediaUrls?.length > 0 && (
                          <div className="flex flex-col gap-1">
                            {message.metadata.mediaUrls.map((media, mIdx) => {
                              const isTwilio = media.url?.startsWith('https://api.twilio.com/');
                              const src = isTwilio ? `${API_BASE}/uploads/twilio-media/${message.id}/${mIdx}` : media.url;
                              const saveKey = `${message.id}-${mIdx}`;
                              const saveState = savingMedia[saveKey];
                              return (
                                <div key={mIdx} className="relative group">
                                  <a href={src} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={src}
                                      alt="MMS attachment"
                                      className="max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                                      style={{ maxHeight: '300px', objectFit: 'contain' }}
                                    />
                                  </a>
                                  {!isOutbound && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleSaveToDocuments(message.id, mIdx); }}
                                      disabled={saveState === 'saving' || saveState === 'saved'}
                                      className={`absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium shadow-md transition-all ${
                                        saveState === 'saved' ? 'bg-green-500 text-white'
                                        : saveState === 'saving' ? 'bg-gray-200 text-gray-500'
                                        : saveState === 'error' ? 'bg-red-500 text-white hover:bg-red-600'
                                        : 'bg-white/90 text-gray-700 hover:bg-white opacity-0 group-hover:opacity-100'
                                      }`}
                                      title="Save to client documents"
                                    >
                                      {saveState === 'saving' ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : saveState === 'saved' ? <><Check className="w-3 h-3" /> Saved</>
                                        : <><FolderPlus className="w-3 h-3" /> Save</>}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {(message.body || message.message || message.text) && (
                          <p className="px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                            {message.body || message.message || message.text}
                          </p>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 px-1 ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-[11px] text-gray-400">
                          {formatTime(message.created_at)}
                        </span>
                        {isOutbound && (
                          <status.icon className={`w-3.5 h-3.5 ${status.color}`} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-4 mb-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 font-bold">
              ×
            </button>
          </div>
        )}

        {/* Log Outcome Button - Show when there are messages */}
        {messages.length > 0 && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <button
              onClick={() => setShowSMSOutcome(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
            >
              <ClipboardCheck className="w-4 h-4" />
              Log Conversation Outcome
            </button>
          </div>
        )}

        {/* Compose */}
        <div className="p-4 bg-white border-t border-gray-200">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <textarea
                ref={inputRef}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                rows={1}
                className="w-full px-4 py-3 bg-gray-100 border-0 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition-all text-[15px]"
                style={{ minHeight: '48px', maxHeight: '120px' }}
                disabled={sending}
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!msg.trim() || sending}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                msg.trim() && !sending
                  ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* SMS Outcome Modal */}
      <SMSOutcomeModal
        isOpen={showSMSOutcome}
        onClose={() => setShowSMSOutcome(false)}
        client={{
          id: clientId,
          firstName: clientName?.split(' ')[0],
          lastName: clientName?.split(' ').slice(1).join(' ')
        }}
        onSave={(result) => {
          // Dispatch event to refresh risk score card
          window.dispatchEvent(new CustomEvent('risk-score-updated', { detail: { clientId } }));
          setShowSMSOutcome(false);
        }}
      />
    </div>
  );
};

export default MessageThread;
